import React, { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { InfoSmallIcon, Select, SelectTrigger, Tooltip } from "@activecollab/components";
import classNames from "classnames";
import PropTypes from "prop-types";
import { addTaskList } from "~/store/actions/actions.taskLists.jsx";
import { getProjectById } from "~/store/selectors/selector.projects";
import { useSecurity } from "./Hooks";

const SelectTaskList = ({ options, selected, onChange, projectId, className, fieldRef, invalid, errorMessage }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const selectedTaskListName = useMemo(() => options.find(({ id }) => id === selected)?.name, [options, selected]);
  const project = useSelector((state) => getProjectById(state, projectId));
  const can = useSecurity();
  const canAddTaskList = can(["can_add_task_list"], project);

  const handleAddNew = useCallback(
    (name) => {
      dispatch(
        addTaskList(projectId, {
          name,
          type: "TaskList",
        })
      ).then(({ response }) => {
        response?.result?.single && typeof onChange === "function" && onChange(response.result.single);
      });
    },
    [dispatch, onChange, projectId]
  );

  return (
    <Select
      target={
        <SelectTrigger
          ref={(el) => {
            if (fieldRef?.current) return (fieldRef.current.task_list_id = el);
          }}
          endAdornment={
            invalid ? (
              <Tooltip title={errorMessage}>
                <InfoSmallIcon
                  fill="var(--red-alert)"
                  className="tw-cursor-help tw-mr-1"
                />
              </Tooltip>
            ) : null
          }
          invalid={invalid}
          className={classNames("tw-w-full", className)}
        >
          {selectedTaskListName}
        </SelectTrigger>
      }
      options={options}
      selected={selected}
      onChange={onChange}
      emptyAction={handleAddNew}
      emptyValue={canAddTaskList && t("Add new task list")}
      noResultText={t("No result")}
      placeholder={t("Choose a task list")}
      disabledInternalSort
      keepSameOptionsOrder
      forceCloseMenu
    />
  );
};

SelectTaskList.propTypes = {
  projectId: PropTypes.number.isRequired,
  selected: PropTypes.number,
  onChange: PropTypes.func,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
    })
  ).isRequired,
  className: PropTypes.string,
  fieldRef: PropTypes.object,
  invalid: PropTypes.bool,
  errorMessage: PropTypes.string,
};

SelectTaskList.defaultProps = {
  invalid: false,
};

export default SelectTaskList;
